import React, { useEffect } from 'react'
import {
  LootboxLimitedDeprecated,
  LootboxType,
  LootboxV2,
} from '@/components/templates/Lootbox/templates/transformLootboxRewards'
import { ASSETS_URL, LOOTBOX_ENV } from '@/constants/constants'
import useSWR from 'swr'
import { create } from 'zustand'
import { createComputed } from 'zustand-computed'
import socket from '@/services/socket'

const fetcher = (url: string) => fetch(url).then((res) => res.json())
export const LootboxProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { data } = useSWR(`${ASSETS_URL}/lootbox/${LOOTBOX_ENV}.json`, fetcher, {
    refreshInterval: 10 * 60 * 1000,
  })

  const isInitialized = useLootboxContext((state) => state.isInitialized)

  useEffect(() => {
    if (data?.lootboxes) {
      useLootboxContext.setState({
        lootboxes: data.lootboxes,
        isInitialized: true,
      })
    }
  }, [data])

  return isInitialized ? children : null
}

type LootboxProviderType = {
  isInitialized: boolean
  lootboxes: Record<string, LootboxV2 | LootboxLimitedDeprecated>
  getLootboxCollection: (contract: string) => LootboxV2 | LootboxLimitedDeprecated
  getBannerLootbox: () => LootboxV2 | LootboxLimitedDeprecated | undefined
  getLootboxesList: () => (LootboxV2 | LootboxLimitedDeprecated)[]
  getLootboxCollectionById: (slug: string) => LootboxV2 | LootboxLimitedDeprecated | undefined
  getLootboxImagePrefix: (contract: string) => string
  getLootboxBalances: (address: string) => Promise<(LootboxLimitedDeprecated & { balance: string })[]>
  getLootboxCollectionByDenom(denom: string): any
}

type LootboxProviderTypeComputer = {}

const computed = createComputed((_state: LootboxProviderType): LootboxProviderTypeComputer => ({}))

export const useLootboxContext = create<LootboxProviderType>()(
  computed((_, get) => ({
    isInitialized: false,
    lootboxes: {},
    getBannerLootbox: () =>
      get()
        .getLootboxesList()
        .find((lootbox) => (lootbox.type === LootboxType.UNLIMITED ? lootbox.metadata.banner : lootbox.banner)),
    getLootboxesList: () =>
      Object.entries(get().lootboxes)
        .map(([_address, lootbox]) => lootbox)
        .sort((a, b) => {
          if (a.type === LootboxType.LIMITED && b.type === LootboxType.LIMITED) {
            return 0
          }

          if (a.type === LootboxType.UNLIMITED && b.type === LootboxType.LIMITED) {
            return -1
          }

          if (a.type === LootboxType.LIMITED && b.type === LootboxType.UNLIMITED) {
            return 1
          }

          if (a.type === LootboxType.UNLIMITED && b.type === LootboxType.UNLIMITED) {
            return a.metadata.priority - b.metadata.priority
          }

          return 0
        }),
    getLootboxImagePrefix: (slug) => {
      if (slug.startsWith('inj1')) {
        return `${ASSETS_URL}/lootbox/${LOOTBOX_ENV}/collections/${slug}/`
      }
      return `${ASSETS_URL}/lootbox/${LOOTBOX_ENV}/collections/`
    },
    getLootboxCollection: (slug) => get().lootboxes[slug],
    getLootboxCollectionById: (id) => {
      return Object.entries(get().lootboxes)
        .find(([_, lootbox]) => lootbox.type === LootboxType.UNLIMITED && lootbox.id === id)
        ?.at(1) as LootboxV2
    },
    getLootboxCollectionByDenom: (_denom: string) => {
      return ''
    },
    getLootboxBalances: async (address) => {
      const balances = await socket.emitWithAck('account:getBalance', address)

      return get()
        .getLootboxesList()
        .filter((lootbox) => lootbox.type === LootboxType.LIMITED && !!balances.coins[lootbox.open.price.denom])
        .map((lootbox) => {
          const box = lootbox as LootboxLimitedDeprecated
          return {
            balance: balances.coins[box.open.price.denom]?.amount || '0',
            ...box,
          }
        })
    },
  }))
)
